<template>
    <div class="phu-topbar">
        <SegmentedControl class="phu-topbar__tabs" :options="tabOptions" :value="activeTab" @input="$emit('change-tab', $event)">
            <template #option="{ option }">
                <span v-if="option.badge" class="phu-topbar__badge">{{ option.badge }}</span>
                <span>{{ option.label }}</span>
            </template>
        </SegmentedControl>

        <SearchBar
            v-if="searchable"
            class="phu-topbar__search"
            :search="search"
            :placeholder="$t('phu_production.stock.search')"
            @on-search="onSearch"
            @on-button-click="$emit('search', '')"
        />

        <div v-else class="phu-topbar__actions">
            <label class="phu-topbar__view">
                <span class="phu-topbar__view-label">{{ $t('phu_production.views.switch') }}</span>
                <span class="phu-topbar__control" aria-hidden="true">
                    <span class="phu-topbar__view-value">{{ activeViewLabel }}</span>
                    <i class="i-andy-chevron-input-down i-andy-s phu-topbar__chevron"></i>
                </span>
                <select class="phu-topbar__view-select" :value="activeWorkstationId || ''" :aria-label="$t('phu_production.views.switch')" @change="onWorkstationChange">
                    <option value="">{{ $t('phu_production.views.all') }}</option>
                    <option v-for="workstation in workstations" :key="workstation.id" :value="workstation.id">
                        {{ viewLabel(workstation) }}
                    </option>
                </select>
            </label>

            <form v-if="showDemo" class="phu-topbar__demo" @submit.prevent="submitDemo">
                <label class="phu-topbar__demo-field">
                    <span class="phu-topbar__view-label">{{ $t('phu_production.views.demo_product') }}</span>
                    <span class="phu-topbar__control">
                        <select v-model="demoProductId" class="phu-topbar__select" required>
                            <option v-for="product in demoProducts" :key="product.id" :value="product.id">{{ product.name }}</option>
                        </select>
                        <i class="i-andy-chevron-input-down i-andy-s phu-topbar__chevron" aria-hidden="true"></i>
                    </span>
                </label>
                <label class="phu-topbar__demo-field">
                    <span class="phu-topbar__view-label">{{ $t('phu_production.views.demo_units') }}</span>
                    <input v-model.number="demoUnits" class="phu-topbar__units" type="number" min="1" step="1" required />
                </label>
                <button type="submit" class="phu-topbar__demo-button" :disabled="demoBusy || !demoProductId">
                    {{ $t('phu_production.views.demo_sell') }}
                </button>
            </form>

            <button
                v-if="showTimer"
                type="button"
                class="phu-topbar__timer"
                :class="{ 'phu-topbar__timer--on': showTime }"
                :aria-pressed="showTime ? 'true' : 'false'"
                :aria-label="$t('phu_production.views.time')"
                @click="$emit('toggle-time')"
            >
                <i class="i-andy-menu-timer i-andy-m" aria-hidden="true"></i>
            </button>
        </div>
    </div>
</template>

<script>
import SegmentedControl from '@/components/domain/ui/SegmentedControl.vue'
import SearchBar from '@/components/domain/ui/SearchBar.vue'
import { PHU_TABS } from '@/store/modules/phuProduction'

const MAX_BADGE = 99

export default {
    name: 'PhuTopbar',
    components: { SegmentedControl, SearchBar },
    props: {
        activeTab: { type: String, required: true },
        counters: { type: Object, required: true },
        workstations: { type: Array, required: true },
        activeWorkstationId: { type: Number, default: null },
        searchable: { type: Boolean, default: false },
        search: { type: String, default: '' },
        showTime: { type: Boolean, default: false },
        showDemo: { type: Boolean, default: false },
        demoProducts: { type: Array, default: () => [] },
        demoBusy: { type: Boolean, default: false }
    },
    data() {
        return {
            demoProductId: '',
            demoUnits: 1
        }
    },
    watch: {
        demoProducts: {
            immediate: true,
            handler(products) {
                const stillThere = products.some((product) => product.id === this.demoProductId)
                if (!stillThere) {
                    this.demoProductId = products[0] ? products[0].id : ''
                }
            }
        }
    },
    computed: {
        tabOptions() {
            return Object.values(PHU_TABS).map((tab) => ({
                id: tab,
                label: this.$t(`phu_production.tabs.${tab}`),
                badge: this.formatBadge(this.counters[tab], tab === PHU_TABS.COOKING)
            }))
        },
        showTimer() {
            return this.activeTab === PHU_TABS.HOLDING
        },
        activeViewLabel() {
            const workstation = this.workstations.find((item) => item.id === this.activeWorkstationId)
            return workstation ? this.viewLabel(workstation) : this.$t('phu_production.views.all')
        }
    },
    methods: {
        viewLabel(workstation) {
            return this.$t('phu_production.views.label', { name: workstation.name })
        },
        formatBadge(count, showZero = false) {
            if (!count) {
                return showZero ? '0' : ''
            }
            return count > MAX_BADGE ? `+${MAX_BADGE}` : String(count)
        },
        submitDemo() {
            const units = Number(this.demoUnits)
            if (!this.demoProductId || !Number.isFinite(units) || units <= 0) {
                return
            }
            this.$emit('demo-sale', { productId: this.demoProductId, units })
        },
        onWorkstationChange(event) {
            const value = event.target.value
            this.$emit('change-workstation', value ? Number(value) : null)
        },
        onSearch(event) {
            this.$emit('search', event.target.value)
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-topbar {
    @include display-flex();
    @include align-items(center);
    @include justify-content(space-between);
    gap: 12px;

    &__badge {
        min-width: 16px;
        padding: 2px;
        border-radius: 99px;
        background-color: $color-error-500;
        font-family: $text-bold;
        font-size: 0.75rem;
        line-height: normal;
        text-align: center;
        color: $color-white;
    }

    &__search {
        width: 450px;
        max-width: 50%;
        padding-bottom: 0;

        ::v-deep .search-container {
            padding-bottom: 0;
        }
    }

    &__actions {
        @include display-flex();
        @include align-items(center);
        gap: $phu-gap;
    }

    &__demo {
        @include display-flex();
        @include align-items(flex-end);
        gap: 8px;
    }

    &__demo-field {
        @include display-flex();
        @include flex-direction(column);
    }

    &__units {
        width: 72px;
        min-height: 36px;
        padding: 4px 8px;
        border: 1px solid $phu-border;
        border-radius: $phu-radius-s;
        background-color: $color-white;
        font-family: $text;
        font-size: 0.875rem;
        color: $phu-text;
    }

    &__demo-button {
        min-height: 36px;
        padding: 8px 12px;
        border: none;
        border-radius: $phu-radius-s;
        background-color: $phu-primary;
        font-family: $text-bold;
        font-size: 0.875rem;
        color: $color-white;
        cursor: pointer;

        &:disabled {
            opacity: 0.4;
            cursor: default;
        }
    }

    &__view {
        position: relative;
        @include display-flex();
        @include flex-direction(column);
        @include justify-content(center);
        min-height: $phu-touch-target;
        padding: 2px 8px;
        border-radius: $phu-radius-s;
        background-color: $phu-surface;
        margin: 0;
        cursor: pointer;
    }

    &__view-value {
        padding-right: 20px;
        font-family: $text-bold;
        font-size: 0.875rem;
        line-height: 18px;
        color: $phu-text;
    }

    &__view-select {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
    }

    &__view-label {
        font-family: $text;
        font-size: 0.75rem;
        color: $phu-text-muted;
    }

    &__control {
        position: relative;
        @include display-flex();
        @include align-items(center);
    }

    &__select {
        width: 100%;
        padding: 0 20px 0 0;
        border: none;
        background: transparent;
        font-family: $text-bold;
        font-size: 0.875rem;
        line-height: 18px;
        color: $phu-text;
        -webkit-appearance: none;
        appearance: none;
        cursor: pointer;
    }

    &__chevron {
        position: absolute;
        top: 1px;
        right: 0;
        color: $neutro-s50;
        pointer-events: none;
    }

    &__timer {
        box-sizing: border-box;
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        width: 40px;
        height: 40px;
        padding: 8px;
        border: none;
        border-radius: $phu-radius-s;
        background-color: $main-t90;
        color: $main-dark;
        cursor: pointer;
        transition: background-color 0.3s ease;

        i {
            color: $main-dark;
            transition: color 0.3s ease;
        }

        &--on {
            background-color: $main-dark;

            i {
                color: $color-white;
            }
        }
    }
}
</style>
